import type { BusinessTerm } from "./business-transaction-terminology.js";
import type { PurchaseOrderStatus } from "./procurement.js";
import type {
  PurchaseReceiptProjectionStatus,
  PurchaseReceiptWriteResult,
} from "./purchase-order-receiving.js";
import type {
  PurchasePayableReconciliationWriteResult,
  SupplierPaymentMethod,
} from "./purchase-payable.js";
import type { PurchaseReturnWriteResult } from "./purchase-return.js";
import type { SupplierBatchPaymentWriteResult } from "./supplier-batch-payment.js";
import type { SupplierCreditWriteResult } from "./supplier-credit-settlement.js";
import type { SupplierSettlementLedgerEntryKind } from "./supplier-settlement-ledger.js";

export type PurchaseCloudProjectionStatus =
  | PurchaseReceiptProjectionStatus
  | PurchaseReceiptWriteResult["cloudProjectionStatus"]
  | PurchasePayableReconciliationWriteResult["cloudProjectionStatus"]
  | PurchaseReturnWriteResult["cloudProjectionStatus"]
  | SupplierBatchPaymentWriteResult["cloudProjectionStatus"]
  | SupplierCreditWriteResult["cloudProjectionStatus"];

export const purchaseOrderStatusTerms = {
  DRAFT: {
    label: "采购草稿",
    explanation: "这里只保存采购计划，尚未提交审批，也不会增加库存或形成应付。",
    nextStep: "请核对供应商、商品、数量和金额后再提交审批。",
  },
  PENDING_APPROVAL: {
    label: "等待采购审批",
    explanation: "采购申请已经提交，正在等待另一位有权限的负责人处理。",
    nextStep: "审批完成前不能收货，请到统一审批中心查看结果。",
  },
  APPROVED: {
    label: "已批准，待收货",
    explanation: "采购单已经批准，可以按实际到货数量办理收货。",
    nextStep: "请核对实到商品、数量、批次和金额后办理收货。",
  },
  REJECTED: {
    label: "采购申请已驳回",
    explanation: "负责人没有批准这份采购申请，当前不能办理收货。",
    nextStep: "请查看驳回原因，修改采购草稿后重新提交。",
  },
  PARTIALLY_RECEIVED: {
    label: "已部分收货",
    explanation: "采购单已有部分商品入库，剩余商品仍可继续收货。",
    nextStep: "请继续按实际到货收货，或确认剩余商品不再到货后办理少收结束。",
  },
  RECEIVED: {
    label: "已全部收货",
    explanation: "采购单批准的商品数量已经全部完成收货。",
    nextStep: "请到采购应付页面继续核对发票和付款情况。",
  },
  CANCELLED: {
    label: "采购单已取消",
    explanation: "这张采购单已经取消，不能继续审批或收货。",
    nextStep: "如仍需采购，请重新建立采购草稿。",
  },
  CLOSED: {
    label: "已按少收关闭",
    explanation: "采购单已经结束，未到货的剩余数量不再继续收货。",
    nextStep: "请按已经实际收货的数量核对库存和应付。",
  },
} satisfies Record<PurchaseOrderStatus, BusinessTerm>;

export const purchaseCloudProjectionTerms = {
  PENDING: {
    label: "云端同步中",
    explanation: "本店权威结果已经生效，云端尚未确认收到同一笔记录。",
    nextStep: "请保留原记录并等待同步，不要重复提交。",
  },
  SYNCED: {
    label: "云端已同步",
    explanation: "本店权威结果和云端记录已经确认一致。",
    nextStep: "可以按当前业务结果继续后续操作。",
  },
  NEEDS_REVIEW: {
    label: "单条云端记录待复核",
    explanation: "只有这一条云端记录需要核对，本店结果已经保留，整套经营系统并未离线。",
    nextStep: "请勿重复提交，其他经营业务可以继续。",
  },
} satisfies Record<PurchaseCloudProjectionStatus, BusinessTerm>;

export const supplierPaymentMethodValues = [
  "BANK_TRANSFER",
  "CASH",
  "OTHER",
] as const satisfies readonly SupplierPaymentMethod[];

export const supplierPaymentMethodTerms = {
  BANK_TRANSFER: {
    label: "银行转账",
    explanation: "这笔款已经通过银行账户在系统外实际转出或到账。",
    nextStep: "请填写银行流水号或回单号，便于日后核对。",
  },
  CASH: {
    label: "现金",
    explanation: "这笔款已经在系统外以现金实际交付或收到。",
    nextStep: "请只登记已经完成的现金交接；现金可以不填外部凭证。",
  },
  OTHER: {
    label: "其他外部方式",
    explanation: "这笔款已经通过银行转账和现金以外的方式在系统外完成。",
    nextStep: "请填写能够在外部核对的凭证号。",
  },
} satisfies Record<SupplierPaymentMethod, BusinessTerm>;

export const supplierSettlementEntryTerms = {
  PURCHASE_RECEIPT: {
    label: "采购收货",
    explanation: "收货入账后，增加这张收货单的本店待付。",
    nextStep: "请继续核对供应商发票和未结应付。",
  },
  PURCHASE_RETURN: {
    label: "采购退货",
    explanation:
      "退货会减少这张收货单当前待付；退货金额超过当前待付时整笔拒绝。已经完成正式发票对账后，请改用供应商贷项。",
    nextStep: "请从原收货单核对库存扣减和应付冲减结果。",
  },
  PAYABLE_RECONCILIATION: {
    label: "发票对账",
    explanation: "按供应商发票核对后，更新这张收货单的正式应付。",
    nextStep: "请按对账后的未结金额继续登记外部付款。",
  },
  SUPPLIER_PAYMENT: {
    label: "单张付款",
    explanation: "登记已经在系统外实际支付的一张收货款。",
    nextStep: "请核对付款凭证和这张收货单的剩余未结金额。",
  },
  SUPPLIER_BATCH_PAYMENT: {
    label: "合并付款",
    explanation: "登记一笔覆盖多张收货单、已在系统外实际完成的付款。",
    nextStep: "请查看付款详情，逐张核对系统分配和剩余未结金额。",
  },
  SUPPLIER_CREDIT: {
    label: "供应商贷项",
    explanation: "确认供应商抵减金额，并按结果减少本店待付或形成供应商应退。",
    nextStep: "请核对供应商贷项凭证和两项余额。",
  },
  SUPPLIER_REFUND: {
    label: "供应商退款",
    explanation: "登记供应商已经在系统外实际退回的款项。",
    nextStep: "请核对到账凭证和供应商仍应退金额。",
  },
  SUPPLIER_PAYMENT_REVERSAL: {
    label: "付款冲正",
    explanation: "撤销原付款登记，并恢复对应收货单的余额。",
    nextStep: "请核对原付款、冲正原因和恢复后的未结金额。",
  },
} satisfies Record<SupplierSettlementLedgerEntryKind, BusinessTerm>;

export function purchaseOrderStatusTerm(status: PurchaseOrderStatus): BusinessTerm {
  return purchaseOrderStatusTerms[status];
}

export function purchaseCloudProjectionTerm(status: PurchaseCloudProjectionStatus): BusinessTerm {
  return purchaseCloudProjectionTerms[status];
}

export function supplierPaymentMethodTerm(method: SupplierPaymentMethod): BusinessTerm {
  return supplierPaymentMethodTerms[method];
}

export function supplierSettlementEntryTerm(kind: SupplierSettlementLedgerEntryKind): BusinessTerm {
  return supplierSettlementEntryTerms[kind];
}
